/**
 * health.js
 *
 * computeHealth(trial) → { score: 'green'|'amber'|'red', label, loginDays, features }
 *
 * 2-axis scoring:
 *   Login recency  × Feature depth
 *
 * Login recency axis (from trialLastLogin — max of Clerk last_sign_in and
 * the Amplitude-derived last_login):
 *   ≤ 3 days → 2
 *   4–7 days → 1
 *   > 7 days → 0
 *
 * Feature depth axis (from trial.features_used_count — distinct product
 * features used in the trailing 30 days, 0–5: chat / protege / workflows /
 * events / sheets — with the features_used list as fallback):
 *   ≥ 3 features → 2
 *   2 features   → 1
 *   0–1 features → 0
 *
 * Combined score:
 *   4 → green
 *   2–3 → amber
 *   0–1 → red
 *
 * Thresholds are overridable via settings (healthLoginGreenDays,
 * healthLoginAmberDays, healthFeatureGreenCount, healthFeatureAmberCount).
 */

const MS_PER_DAY = 86_400_000;

/**
 * Effective last login for a trial row (IMP-1818): most recent of Clerk's
 * last_sign_in_at (exact, merged by the roster sync) and the Amplitude-derived
 * last_login (week-granularity, only updates when its manual pipeline runs).
 * Both are lower bounds on "last seen" — Clerk misses activity on a
 * long-lived session, Amplitude misses everything between pipeline runs —
 * so take the max. Returns an ISO string or null.
 */
export function trialLastLogin(t) {
  const times = [t?.clerk_last_sign_in, t?.last_login]
    .map((v) => (v ? new Date(v).getTime() : NaN))
    .filter(Number.isFinite);
  return times.length ? new Date(Math.max(...times)).toISOString() : null;
}

function daysSince(dateStr) {
  if (!dateStr) return Infinity;
  const d = new Date(dateStr);
  if (isNaN(d)) return Infinity;
  return (Date.now() - d.getTime()) / MS_PER_DAY;
}

function loginScore(lastLoginDate, greenDays, amberDays) {
  const days = daysSince(lastLoginDate);
  if (days <= greenDays) return 2;
  if (days <= amberDays) return 1;
  return 0;
}

// Distinct features used in the trailing 30 days — the synced count when
// present, else the length of the features_used list.
export function trialFeatureCount(trial) {
  const raw = trial?.features_used_count;
  if (raw !== undefined && raw !== null && String(raw).trim() !== '') {
    return parseInt(raw, 10) || 0;
  }
  if (trial?.features_used) {
    return String(trial.features_used).split(',').map((s) => s.trim()).filter(Boolean).length;
  }
  return 0;
}

function featureScore(count, greenCount, amberCount) {
  if (count >= greenCount) return 2;
  if (count >= amberCount) return 1;
  return 0;
}

export function computeHealth(trial, settings = {}) {
  if (!trial) return { score: 'red', label: 'No data', loginDays: null, features: 0 };

  const greenDays  = Number(settings.healthLoginGreenDays  ?? 3);
  const amberDays  = Number(settings.healthLoginAmberDays  ?? 7);
  // Feature axis is 0–5 (distinct feature families): 3+ of 5 is deep usage.
  const greenCount = Number(settings.healthFeatureGreenCount ?? 3);
  const amberCount = Number(settings.healthFeatureAmberCount ?? 2);

  const lastLogin = trialLastLogin(trial);
  const features  = trialFeatureCount(trial);

  const lScore = loginScore(lastLogin, greenDays, amberDays);
  const fScore = featureScore(features, greenCount, amberCount);
  const combined = lScore + fScore;

  const loginDays = daysSince(lastLogin);

  let score, label;
  if (combined >= 4) {
    score = 'green';
    label = 'Healthy';
  } else if (combined >= 2) {
    score = 'amber';
    label = 'At Risk';
  } else {
    score = 'red';
    label = 'Churning';
  }

  return {
    score,
    label,
    loginDays: isFinite(loginDays) ? Math.round(loginDays) : null,
    features,
  };
}
